import { DatePipe } from "@angular/common";
import { ChangeDetectionStrategy, Component, inject, signal, viewChild } from "@angular/core";
import { Router } from "@angular/router";

import { Bell, Check, CheckCheck, LucideAngularModule, X } from "lucide-angular";

import { DocumentationRoutesConstants } from "@documentation/routes/documentation.routes.constants";
import { NgbPopover, NgbPopoverModule } from "@ng-bootstrap/ng-bootstrap";
import { PopoverConfigDirective } from "@shared/directives/popover-config.directive";

import { NotificationModel, NotificationType } from "./models/notification.model";
import { NotificationHttpService } from "./services/notification-http.service";

@Component({
  selector: "component-notifications",
  imports: [LucideAngularModule, DatePipe, NgbPopoverModule, PopoverConfigDirective],
  templateUrl: "./notifications.component.html",
  styleUrl: "./notifications.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationsComponent {
  private readonly _notificationService = inject(NotificationHttpService);
  private readonly _router = inject(Router);

  protected readonly notifications = signal<NotificationModel[]>([]);
  protected readonly unreadCount = signal<number>(0);
  protected readonly loading = signal<boolean>(false);
  protected readonly popover = viewChild(NgbPopover);

  protected readonly BellIcon = Bell;
  protected readonly CloseIcon = X;
  protected readonly CheckIcon = Check;
  protected readonly CheckAllIcon = CheckCheck;

  protected readonly popoverConfig = {
    modifiers: [
      {
        name: "offset",
        options: {
          offset: [-30, 16]
        }
      }
    ]
  };

  ngOnInit(): void {
    this.refreshCount();
  }

  refreshCount(): void {
    this._notificationService.getUnreadCount().subscribe({
      next: (res) => this.unreadCount.set(res.count),
      error: () => {} // Silent error
    });
  }

  loadNotifications(): void {
    this.loading.set(true);
    this._notificationService.findAll().subscribe({
      next: (data) => {
        this.notifications.set(data);
        this.loading.set(false);
      },
      error: () => this.loading.set(false)
    });
  }

  onTogglePopover(): void {
    if (this.popover()?.isOpen()) {
      this.popover()?.close();
    } else {
      this.popover()?.open();
      this.loadNotifications();
    }
  }

  markAsRead(notification: NotificationModel, event?: Event): void {
    event?.stopPropagation();
    if (notification.isRead) return;

    // Optimistic update
    this.notifications.update((list) => list.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)));
    this.unreadCount.update((c) => Math.max(0, c - 1));

    this._notificationService.markAsRead(notification.id).subscribe();
  }

  onHover(notification: NotificationModel): void {
    this.markAsRead(notification);
  }

  markAllAsRead(): void {
    const unread = this.notifications().filter((n) => !n.isRead);
    if (unread.length === 0) return;

    // Optimistic
    this.notifications.update((list) => list.map((n) => ({ ...n, isRead: true })));
    this.unreadCount.set(0);

    this._notificationService.markAllAsRead().subscribe();
  }

  deleteNotification(id: string, event: Event): void {
    event.stopPropagation();

    const notif = this.notifications().find((n) => n.id === id);
    // Optimistic
    this.notifications.update((list) => list.filter((n) => n.id !== id));
    if (notif && !notif.isRead) {
      this.unreadCount.update((c) => Math.max(0, c - 1));
    }

    this._notificationService.deleteNotification(id).subscribe();
  }

  handleNotificationClick(notification: NotificationModel): void {
    this.markAsRead(notification);

    if (notification.type === NotificationType.DOCUMENT_SHARE) {
      const docId = notification.metadata?.documentId;
      if (docId) {
        this._router.navigate([DocumentationRoutesConstants.document(docId)]);
        this.popover()?.close();
      }
    }
  }
}
